
import { sendConfirmationEmail as sendEmail } from './emailService';
import { toast } from '@/hooks/use-toast';

// Constants for retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 2000; // 2 seconds

/**
 * Enhanced email sending service with automatic retries
 * Uses exponential backoff strategy for retries
 */
export const sendConfirmationEmailWithRetry = async (
  attendeeData: any,
  onSuccess?: () => void,
  onError?: (error: string) => void
): Promise<{ success: boolean; error?: string }> => {
  let retryCount = 0;
  let lastError: string | null = null;
  
  // Try to send email until max retries reached
  while (retryCount <= MAX_RETRIES) {
    try {
      // If this is a retry, show toast notification
      if (retryCount > 0) {
        console.log(`Retrying email send (attempt ${retryCount} of ${MAX_RETRIES})...`);
        toast({
          title: `Đang thử lại gửi email (lần ${retryCount}/${MAX_RETRIES})`,
          description: "Hệ thống đang cố gắng gửi lại email xác nhận...",
          variant: "info"
        });
      }
      
      // Attempt to send the email
      const result = await sendEmail(attendeeData);
      
      // If successful, call the success callback and return success
      if (result.success) {
        if (onSuccess) onSuccess();
        
        // Only show a "retry successful" toast if this was actually a retry
        if (retryCount > 0) {
          toast({
            title: "Gửi email thành công!",
            description: "Email xác nhận đã được gửi sau khi thử lại.",
            variant: "success"
          });
        }
        
        return { success: true };
      } else {
        // If we got here, the send function returned success: false
        throw new Error(result.error || "Unknown error");
      }
    } catch (error) {
      // Record the error and increment retry counter
      lastError = error instanceof Error ? error.message : 'Lỗi không xác định';
      retryCount++;
      
      // If we've reached max retries, break out of the loop
      if (retryCount > MAX_RETRIES) break;
      
      // Exponential backoff: wait longer between each retry
      const delay = INITIAL_RETRY_DELAY * Math.pow(2, retryCount - 1);
      console.log(`Email send failed. Waiting ${delay}ms before retry ${retryCount}...`);
      
      // Wait before trying again
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  // If we got here, all retries failed
  console.error(`Failed to send email after ${MAX_RETRIES} attempts. Last error:`, lastError);
  
  // Call error callback if provided
  if (onError && lastError) {
    onError(lastError);
  }
  
  // Show failure toast only once after all retries
  toast({
    title: "Không thể gửi email",
    description: "Đã thử gửi email nhiều lần nhưng không thành công. Vui lòng tải xuống mã QR.",
    variant: "destructive"
  });
  
  return { 
    success: false, 
    error: lastError || 'Email could not be sent after multiple attempts' 
  };
};
